import {
  AuditOutlined, CarOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CreditCardOutlined,
  HistoryOutlined, PrinterOutlined, ProfileOutlined, SaveOutlined, SolutionOutlined,
  UserOutlined, BuildOutlined, ToolOutlined
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Dropdown, Empty, Segmented, Space, Spin, Tag, Timeline, Typography, message, theme } from "antd";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatCurrency, formatDate, formatDateTime, formatMileage, formatPhone } from "../../shared/utils/formatters";
import { customerName } from "../customers/customer-types";
import { repairOrdersApi } from "./repair-orders-api";
import { RepairOrderStatusTag } from "./repair-order-status";
import {
  BusinessOperationsSection, EstimateSection, InspectionSection, InvoiceSection, PaymentSection,
  ServiceIntelligencePanel, SettlementSummarySection,
  ShopWorkSection, SupplementApprovalPanel
} from "./RepairWorkflowSections";
import { ScanButton } from "../scan/ScanButton";
import type { WorkflowData } from "./repair-order-types";

export function RepairWorkspacePage() {
  const { id = "", section } = useParams();
  const { text } = useAppConfig();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messageApi, messageContext] = message.useMessage();
  const workspace = useQuery({ queryKey: ["repair-workspace", id], queryFn: () => repairOrdersApi.workspace(id) });
  const workflow = useQuery({ queryKey: ["repair-workflow", id], queryFn: () => repairOrdersApi.workflow(id) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["repair-workspace", id] });
    void queryClient.invalidateQueries({ queryKey: ["repair-workflow", id] });
    void queryClient.invalidateQueries({ queryKey: ["repair-orders"] });
  };
  const savePage = useMutation({
    mutationFn: () => {
      const current = workflow.data?.repairOrder;
      if (!current) throw new Error("Repair order is not loaded");
      return repairOrdersApi.updateWorkflowContext(id, {
        priority: current.priority,
        ...(current.advisorId ? { advisorId: current.advisorId } : {}),
        ...(current.technicianId ? { technicianId: current.technicianId } : {}),
        warranty: current.warranty,
        ...(current.warrantyNotes ? { warrantyNotes: current.warrantyNotes } : {})
      });
    },
    onSuccess: () => {
      refresh();
      messageApi.success(text.pageSaved);
    }
  });
  if (workspace.isLoading || workflow.isLoading) return <div className="workspace-loading"><Spin size="large" /></div>;
  if (!workspace.data || !workflow.data) return <Empty />;
  const data = workspace.data;
  const workflowData = workflow.data;
  const order = workflowData.repairOrder;
  const labels = text as unknown as Record<string, string>;
  const goToSection = (next: string) => navigate(`/repair-orders/${id}/workspace/${next}`, { replace: true });
  const printDocument = (type: "estimate" | "workshop" | "settlement") => {
    const target = type === "estimate" ? "estimate" : "shop-work";
    const documentType = type === "estimate" ? "ESTIMATE" : type === "workshop" ? "WORK_ORDER" : "INVOICE";
    void repairOrdersApi.logDocumentPrint(id, documentType).finally(() => {
      navigate(`/repair-orders/${id}/workspace/${target}?print=${type}&printAt=${Date.now()}`);
    });
  };

  const workspaceStyle = {
    "--workspace-surface": token.colorBgContainer,
    "--workspace-canvas": token.colorBgLayout,
    "--workspace-border": token.colorBorderSecondary,
    "--workspace-secondary": token.colorTextSecondary,
    "--workspace-primary": token.colorPrimary
  } as CSSProperties;

  return <section className="repair-workspace" style={workspaceStyle}>
    {messageContext}
    <header className="workspace-command-header">
      <div className="workspace-identity">
        <Typography.Text>{text.repairWorkspace}</Typography.Text>
        <Space size="small"><Typography.Title level={2}>{order.roNumber}</Typography.Title><ScanButton repairOrderId={id} compact /></Space>
      </div>
      <HeaderFact label={text.customer} value={customerName(order.customer)} icon={<UserOutlined />} />
      <HeaderFact label={text.vehicle} value={`${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model}`} icon={<CarOutlined />} />
      <HeaderFact label={text.repairStatus} value={<RepairOrderStatusTag status={order.status} />} />
      <HeaderFact label={text.priority} value={labels[`${order.priority.toLowerCase()}Priority`] ?? order.priority} />
      <HeaderFact label={text.advisor} value={order.advisor?.displayName ?? text.unassigned} />
      <HeaderFact label={text.technician} value={order.technician?.displayName ?? text.unassigned} />
    </header>

    <div className="workspace-frame">
      <main className="workspace-main" aria-live="polite">
        <div className="workspace-content-heading">
          <div><Typography.Text type="secondary">{text.repairOperatingSystem}</Typography.Text><Typography.Title level={3}>{text.repairEventExperience}</Typography.Title></div>
          <Typography.Text type="secondary">{order.roNumber}</Typography.Text>
        </div>
        <SupplementApprovalPanel id={id} data={workflowData} text={labels} refresh={refresh} />
        <ContinuousRepairEvent
          id={id}
          workspace={data}
          workflow={workflowData}
          text={labels}
          refresh={refresh}
          requestedSection={section}
        />
      </main>

      <aside className="workspace-summary">
        <SummaryBlock icon={<CarOutlined />} title={text.vehicleSummary}>
          <b>{order.vehicle.year} {order.vehicle.make} {order.vehicle.model}</b>
          <span className="mono">{order.vehicle.vin}</span>
          <span>{order.vehicle.licensePlate || "—"}</span>
        </SummaryBlock>
        <SummaryBlock icon={<UserOutlined />} title={text.customerSummary}>
          <b>{customerName(order.customer)}</b>
          <span>{formatPhone(order.customer.phone)}</span>
          <span>{order.customer.email || "—"}</span>
        </SummaryBlock>
        <SummaryBlock icon={<CheckCircleOutlined />} title={text.currentStatus}>
          <RepairOrderStatusTag status={order.status} />
        </SummaryBlock>
        <SummaryMetric label={text.mileage} value={formatMileage(order.mileageOut ?? order.mileageIn)} />
        <SummaryMetric
          label={text.warranty}
          value={order.services.some(service => service.warrantyRecord)
            ? `${order.services.filter(service => service.warrantyRecord).length} ${text.service}`
            : order.warranty ? (order.warrantyNotes || text.yes) : text.no}
        />
        <SummaryMetric label={text.openBalance} value={formatCurrency(order.invoice?.balance ?? 0)} emphasized />
        <SummaryBlock icon={<ClockCircleOutlined />} title={text.timeline}>
          <Timeline className="workspace-mini-timeline" items={order.timeline.slice(0, 5).map(item => ({
            color: item.toStatus === "CANCELLED" ? "red" : item.eventType.includes("supplement") ? "orange" : "blue",
            children: <><b>{item.title}</b><small>{formatDateTime(item.createdAt)}</small></>
          }))} />
        </SummaryBlock>
      </aside>
    </div>

    <footer className="workspace-action-bar" aria-label={text.workspaceActions}>
      <div className="workspace-action-context"><span>{order.roNumber}</span><RepairOrderStatusTag status={order.status} /></div>
      <Space wrap>
        <Button icon={<SaveOutlined />} loading={savePage.isPending} onClick={() => savePage.mutate()}>{text.save}</Button>
        <Button
          icon={<CheckCircleOutlined />}
          disabled={!["WAITING_APPROVAL", "SUPPLEMENT_REQUIRED"].includes(order.status)}
          onClick={() => goToSection("estimate")}
        >{order.status === "SUPPLEMENT_REQUIRED" ? text.approveSupplement : text.approve}</Button>
        <Dropdown trigger={["click"]} menu={{
          items: [
            { key: "estimate", label: text.printEstimate },
            { key: "workshop", label: text.printWorkshopOrder },
            { key: "settlement", label: text.printSettlement }
          ],
          onClick: ({ key }) => printDocument(key as "estimate" | "workshop" | "settlement")
        }}>
          <Button icon={<PrinterOutlined />}>{text.print}</Button>
        </Dropdown>
        <Button
          type="primary"
          icon={<CarOutlined />}
          disabled={order.status !== "PAID"}
          onClick={() => goToSection("commercial-close")}
        >{text.deliverVehicle}</Button>
      </Space>
    </footer>
  </section>;
}

function HeaderFact({ label, value, icon }: { label: string; value: ReactNode; icon?: ReactNode }) {
  return <div className="workspace-header-fact">
    <small>{icon}{label}</small>
    <div>{value}</div>
  </div>;
}

function SummaryBlock({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return <section className="summary-block">
    <div className="summary-title">{icon}<span>{title}</span></div>
    <div className="summary-content">{children}</div>
  </section>;
}

function SummaryMetric({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) {
  return <div className={`summary-metric${emphasized ? " emphasized" : ""}`}><span>{label}</span><b>{value}</b></div>;
}

function ContinuousRepairEvent({
  requestedSection, workspace, workflow, text, refresh, id
}: {
  requestedSection?: string;
  workspace: Awaited<ReturnType<typeof repairOrdersApi.workspace>>;
  workflow: WorkflowData;
  text: Record<string, string>;
  refresh: () => void;
  id: string;
}) {
  const props = { id, data: workflow, text, refresh };
  const t = (key: string) => text[key] ?? key;
  const order = workflow.repairOrder;
  const focus = legacySectionAnchor(requestedSection);
  const [timelineFilter, setTimelineFilter] = useState("ALL");
  const filteredTimeline = workspace.repairOrder.timeline.filter(item =>
    timelineFilter === "ALL" || timelineCategory(item.eventType) === timelineFilter
  );
  useEffect(() => {
    if (!focus) return;
    window.requestAnimationFrame(() => document.getElementById(focus)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [focus]);
  return <div className="repair-event-story">
    <div className="repair-event-next-action">
      <div>
        <span>{t("nextAction")}</span>
        <b>{t(nextRepairAction(order.status))}</b>
        <small>{t("whatHappensNextForVehicle")}</small>
      </div>
      <RepairOrderStatusTag status={order.status} />
    </div>
    <div className="repair-role-strip" aria-label={t("roleWorkflowFocus")}>
      {[
        { role: "ADVISOR", label: t("roleADVISOR"), focus: t("advisorWorkflowFocus") },
        { role: "TECHNICIAN", label: t("roleTECHNICIAN"), focus: t("technicianWorkflowFocus") },
        { role: "WAREHOUSE", label: t("roleWAREHOUSE"), focus: t("partsWorkflowFocus") },
        { role: "OWNER", label: t("owner"), focus: t("ownerWorkflowFocus") }
      ].map(item => <div key={item.role} className={ownerRoleForStatus(order.status) === item.role ? "active" : ""}>
        <b>{item.label}</b><span>{item.focus}</span>
      </div>)}
    </div>

    <StorySection id="concern" number="01" title={t("visitReason")} subtitle={t("visitReasonHint")} icon={<ProfileOutlined />}>
      <div className="concern-story-list">
        {(workspace.repairOrder.concerns.length
          ? workspace.repairOrder.concerns
          : [{
              id: "legacy", sequence: 1, customerWords: order.customerConcern || "—",
              advisorSummary: null, source: "LEGACY", resolvedStatus: "OPEN",
              createdAt: order.createdAt, createdBy: null
            }]
        ).map(concern => <Card key={concern.id} size="small">
          <div className="concern-story-head">
            <Tag color="blue">{t("customerWords")}</Tag>
            <small>{formatDateTime(concern.createdAt)}</small>
          </div>
          <Typography.Title level={4}>{concern.customerWords}</Typography.Title>
          <Typography.Text type="secondary">
            {concern.advisorSummary || t("awaitingAdvisorClarification")}
          </Typography.Text>
        </Card>)}
      </div>
    </StorySection>

    <StorySection id="inspection" number="02" title={t("whatWeFound")} subtitle={t("whatWeFoundHint")} icon={<SolutionOutlined />}>
      <InspectionSection {...props} />
    </StorySection>

    <StorySection id="services" number="03" title={t("whatWeRecommend")} subtitle={t("serviceCardsAreWorkUnits")} icon={<BuildOutlined />}>
      <ServiceIntelligencePanel {...props} />
    </StorySection>

    <StorySection id="approval" number="04" title={t("customerDecision")} subtitle={t("customerDecisionHint")} icon={<CheckCircleOutlined />}>
      <EstimateSection {...props} />
    </StorySection>

    <StorySection id="work" number="05" title={t("shopExecution")} subtitle={t("shopExecutionHint")} icon={<ToolOutlined />}>
      <ShopWorkSection {...props} />
    </StorySection>

    <StorySection id="commercial-close" number="06" title={t("commercialClose")} subtitle={t("commercialCloseHint")} icon={<CreditCardOutlined />}>
      <SettlementSummarySection {...props} />
      <div className="commercial-close-grid">
        <InvoiceSection data={workflow} text={text} />
        <PaymentSection {...props} />
      </div>
      <BusinessOperationsSection {...props} />
    </StorySection>

    <StorySection id="timeline" number="07" title={t("timelineV2")} subtitle={t("timelineImmutableHint")} icon={<ClockCircleOutlined />}>
      <Card className="repair-event-timeline-card">
        <Segmented
          className="timeline-filter"
          value={timelineFilter}
          onChange={value => setTimelineFilter(String(value))}
          options={["ALL", "CUSTOMER", "WORK", "PARTS", "QC", "COMMERCIAL"].map(value => ({
            value,
            label: t(`timelineFilter${value}`)
          }))}
        />
        <Timeline items={filteredTimeline.map(item => ({
          color: item.toStatus === "CANCELLED" ? "red" : item.eventType.includes("supplement") ? "orange" : "blue",
          children: <><b>{item.title}</b>{item.description && <div>{item.description}</div>}<div className="table-secondary">{item.actor?.displayName ?? "System"} · {formatDateTime(item.createdAt)}</div></>
        }))} />
      </Card>
    </StorySection>

    <StorySection id="history" number="08" title={t("repairHistory")} subtitle={t("repairHistoryHint")} icon={<HistoryOutlined />}>
      <Card>
        {order.serviceHistory.length ? <div className="permanent-history-list">
          {order.serviceHistory.map(item => <article key={item.id}>
            <div><b>{item.serviceName}</b><span>{item.serviceCode}</span></div>
            <div><span>{formatDate(item.performedAt)}</span><span>{formatMileage(item.mileage)}</span><strong>{formatCurrency(Number(item.laborAmount) + Number(item.partsAmount))}</strong></div>
            {item.warranty && <Tag color="blue">{t("warrantyCoverage")}</Tag>}
          </article>)}
        </div> : <Empty description={t("historyCreatedAtDelivery")} />}
      </Card>
    </StorySection>

    <StorySection id="activity-log" number="09" title={t("activityLog")} subtitle={t("forensicAuditHint")} icon={<AuditOutlined />}>
      <Card><Timeline items={workspace.activityLog.map(item => ({
        children: <><b>{item.note || item.action}</b><div className="table-secondary">{item.actor?.displayName ?? "System"} · {formatDateTime(item.createdAt)}</div></>
      }))} /></Card>
    </StorySection>
  </div>;
}

function StorySection({ id, number, title, subtitle, icon, children }: {
  id: string;
  number: string;
  title: string;
  subtitle: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return <section id={id} className="repair-story-section">
    <header>
      <span className="story-step">{number}</span>
      <span className="story-icon">{icon}</span>
      <div><h3>{title}</h3><p>{subtitle}</p></div>
    </header>
    <div className="story-section-content">{children}</div>
  </section>;
}

function legacySectionAnchor(section?: string) {
  if (!section || section === "overview") return "concern";
  if (["inspection", "estimate"].includes(section)) return section === "inspection" ? "inspection" : "approval";
  if (["labor", "parts", "photos", "shop-work"].includes(section)) return "work";
  if (["invoice", "payment", "business", "commercial-close"].includes(section)) return "commercial-close";
  return section;
}

function nextRepairAction(status: string) {
  const actions: Record<string, string> = {
    DRAFT: "completeCheckIn",
    CHECKED_IN: "startInspection",
    DIAGNOSIS: "startInspection",
    ESTIMATE: "buildEstimate",
    WAITING_APPROVAL: "followUpApproval",
    APPROVED: "startRepairWork",
    IN_PROGRESS: "continueRepair",
    SUPPLEMENT_REQUIRED: "approveSupplement",
    WAITING_PARTS: "resolveParts",
    QUALITY_CHECK: "performQc",
    READY_DELIVERY: "collectPayment",
    PAID: "deliverVehicle",
    CLOSED: "viewRepairHistory",
    CANCELLED: "viewRepairHistory"
  };
  return actions[status] ?? "viewRepairHistory";
}

function ownerRoleForStatus(status: string) {
  if (["CHECKED_IN", "DIAGNOSIS", "IN_PROGRESS", "QUALITY_CHECK"].includes(status)) return "TECHNICIAN";
  if (status === "WAITING_PARTS") return "WAREHOUSE";
  if (["ESTIMATE", "WAITING_APPROVAL", "SUPPLEMENT_REQUIRED", "READY_DELIVERY"].includes(status)) return "ADVISOR";
  return "OWNER";
}

function timelineCategory(eventType: string) {
  if (
    eventType.includes("estimate")
    || eventType.includes("approval")
    || eventType.includes("supplement")
    || eventType.includes("customer")
  ) return "CUSTOMER";
  if (eventType.includes("part") || eventType.includes("material") || eventType.includes("inventory")) return "PARTS";
  if (eventType.includes("qc") || eventType.includes("quality")) return "QC";
  if (eventType.includes("invoice") || eventType.includes("payment") || eventType.includes("closed") || eventType.includes("delivered") || eventType.includes("communication")) return "COMMERCIAL";
  return "WORK";
}
